// Structural constants - lumber, span tables, building types

export const BUILDING_TYPE_PRESETS = {
  residential: {
    label: 'Residential',
    description: 'Standard wood-frame construction',
    defaultExteriorWall: 'exterior',
    defaultInteriorWall: 'interior',
    defaultHeight: 96,
  },
  barn: {
    label: 'Barn / Pole Building',
    description: 'Post-frame construction',
    defaultExteriorWall: 'post-6x6',
    defaultInteriorWall: 'post-4x4',
    defaultHeight: 144,
  },
  commercial: {
    label: 'Commercial',
    description: 'Steel or heavy timber frame',
    defaultExteriorWall: 'exterior',
    defaultInteriorWall: 'interior-2x6',
    defaultHeight: 120,
  },
};

export const LUMBER_SPECIES = {
  spf: { label: 'SPF (Spruce-Pine-Fir)', Fb: { '#1': 1150, '#2': 875 }, E: 1400000 },
  douglas_fir: { label: 'Douglas Fir-Larch', Fb: { '#1': 1350, '#2': 1000 }, E: 1700000 },
  southern_pine: { label: 'Southern Pine', Fb: { '#1': 1350, '#2': 1050 }, E: 1700000 },
  hem_fir: { label: 'Hem-Fir', Fb: { '#1': 1100, '#2': 850 }, E: 1500000 },
};

// Actual lumber dimensions (inches)
export const JOIST_SIZES = {
  '2x6':  { label: '2×6',  width: 1.5, depth: 5.5,  S: 7.56,   I: 20.80,  weight: 1.7 },
  '2x8':  { label: '2×8',  width: 1.5, depth: 7.25, S: 13.14,  I: 47.63,  weight: 2.3 },
  '2x10': { label: '2×10', width: 1.5, depth: 9.25, S: 21.39,  I: 98.93,  weight: 2.9 },
  '2x12': { label: '2×12', width: 1.5, depth: 11.25, S: 31.64, I: 177.98, weight: 3.5 },
};

export const LOAD_TYPES = {
  living:   { label: 'Living Areas',    live: 40, dead: 10, deflectionLimit: 360, description: 'Bedrooms, halls, closets, stairs' },
  sleeping: { label: 'Sleeping Rooms',  live: 30, dead: 10, deflectionLimit: 360, description: 'Bedrooms only' },
  storage:  { label: 'Attic/Storage',   live: 20, dead: 10, deflectionLimit: 240, description: 'Limited storage, no habitation' },
  barn_loft:{ label: 'Barn Loft',       live: 40, dead: 15, deflectionLimit: 360, description: 'Hay storage, workshop above' },
  deck:     { label: 'Deck/Balcony',    live: 40, dead: 10, deflectionLimit: 360, description: 'Exterior decks' },
};

// IRC span table (max span in inches)
// Organized by: species → grade → joist size → spacing → load type
// Spans are for simple-span floor joists
export const IRC_SPAN_TABLE = {
  spf: {
    '#1': {
      '2x6':  { 12: { living: 126, sleeping: 138 }, 16: { living: 114, sleeping: 126 }, 24: { living:  96, sleeping: 108 } },
      '2x8':  { 12: { living: 168, sleeping: 180 }, 16: { living: 150, sleeping: 162 }, 24: { living: 132, sleeping: 144 } },
      '2x10': { 12: { living: 210, sleeping: 222 }, 16: { living: 192, sleeping: 204 }, 24: { living: 168, sleeping: 180 } },
      '2x12': { 12: { living: 252, sleeping: 264 }, 16: { living: 228, sleeping: 246 }, 24: { living: 198, sleeping: 216 } },
    },
    '#2': {
      '2x6':  { 12: { living: 120, sleeping: 132 }, 16: { living: 108, sleeping: 120 }, 24: { living:  90, sleeping: 102 } },
      '2x8':  { 12: { living: 156, sleeping: 174 }, 16: { living: 144, sleeping: 156 }, 24: { living: 120, sleeping: 138 } },
      '2x10': { 12: { living: 198, sleeping: 216 }, 16: { living: 180, sleeping: 198 }, 24: { living: 156, sleeping: 174 } },
      '2x12': { 12: { living: 240, sleeping: 258 }, 16: { living: 222, sleeping: 240 }, 24: { living: 186, sleeping: 204 } },
    },
  },
  douglas_fir: {
    '#1': {
      '2x6':  { 12: { living: 132, sleeping: 144 }, 16: { living: 120, sleeping: 132 }, 24: { living: 102, sleeping: 114 } },
      '2x8':  { 12: { living: 174, sleeping: 192 }, 16: { living: 162, sleeping: 174 }, 24: { living: 138, sleeping: 150 } },
      '2x10': { 12: { living: 222, sleeping: 240 }, 16: { living: 204, sleeping: 216 }, 24: { living: 174, sleeping: 192 } },
      '2x12': { 12: { living: 270, sleeping: 282 }, 16: { living: 246, sleeping: 258 }, 24: { living: 210, sleeping: 228 } },
    },
    '#2': {
      '2x6':  { 12: { living: 126, sleeping: 138 }, 16: { living: 114, sleeping: 126 }, 24: { living:  96, sleeping: 108 } },
      '2x8':  { 12: { living: 168, sleeping: 180 }, 16: { living: 150, sleeping: 168 }, 24: { living: 132, sleeping: 144 } },
      '2x10': { 12: { living: 210, sleeping: 228 }, 16: { living: 192, sleeping: 210 }, 24: { living: 168, sleeping: 180 } },
      '2x12': { 12: { living: 258, sleeping: 270 }, 16: { living: 234, sleeping: 252 }, 24: { living: 204, sleeping: 216 } },
    },
  },
  southern_pine: {
    '#1': {
      '2x6':  { 12: { living: 132, sleeping: 144 }, 16: { living: 120, sleeping: 132 }, 24: { living: 102, sleeping: 114 } },
      '2x8':  { 12: { living: 174, sleeping: 192 }, 16: { living: 162, sleeping: 174 }, 24: { living: 138, sleeping: 150 } },
      '2x10': { 12: { living: 222, sleeping: 240 }, 16: { living: 204, sleeping: 216 }, 24: { living: 174, sleeping: 192 } },
      '2x12': { 12: { living: 270, sleeping: 282 }, 16: { living: 246, sleeping: 258 }, 24: { living: 210, sleeping: 228 } },
    },
    '#2': {
      '2x6':  { 12: { living: 126, sleeping: 138 }, 16: { living: 114, sleeping: 132 }, 24: { living: 102, sleeping: 114 } },
      '2x8':  { 12: { living: 174, sleeping: 186 }, 16: { living: 156, sleeping: 168 }, 24: { living: 132, sleeping: 150 } },
      '2x10': { 12: { living: 216, sleeping: 234 }, 16: { living: 198, sleeping: 210 }, 24: { living: 168, sleeping: 186 } },
      '2x12': { 12: { living: 264, sleeping: 276 }, 16: { living: 240, sleeping: 258 }, 24: { living: 204, sleeping: 222 } },
    },
  },
  hem_fir: {
    '#1': {
      '2x6':  { 12: { living: 126, sleeping: 132 }, 16: { living: 114, sleeping: 120 }, 24: { living:  96, sleeping: 102 } },
      '2x8':  { 12: { living: 162, sleeping: 174 }, 16: { living: 150, sleeping: 162 }, 24: { living: 126, sleeping: 138 } },
      '2x10': { 12: { living: 204, sleeping: 222 }, 16: { living: 186, sleeping: 204 }, 24: { living: 162, sleeping: 174 } },
      '2x12': { 12: { living: 246, sleeping: 264 }, 16: { living: 228, sleeping: 240 }, 24: { living: 192, sleeping: 210 } },
    },
    '#2': {
      '2x6':  { 12: { living: 114, sleeping: 126 }, 16: { living: 108, sleeping: 114 }, 24: { living:  90, sleeping:  96 } },
      '2x8':  { 12: { living: 156, sleeping: 168 }, 16: { living: 138, sleeping: 150 }, 24: { living: 120, sleeping: 132 } },
      '2x10': { 12: { living: 198, sleeping: 210 }, 16: { living: 174, sleeping: 192 }, 24: { living: 150, sleeping: 168 } },
      '2x12': { 12: { living: 234, sleeping: 252 }, 16: { living: 216, sleeping: 234 }, 24: { living: 180, sleeping: 198 } },
    },
  },
};

// Engineered lumber options
export const ENGINEERED_LUMBER = {
  lvl: {
    label: 'LVL (Laminated Veneer Lumber)',
    sizes: {
      '1.75x7.25':  { label: '1¾×7¼ LVL',  maxSpan: { living: 168, sleeping: 180 } },
      '1.75x9.25':  { label: '1¾×9¼ LVL',  maxSpan: { living: 216, sleeping: 234 } },
      '1.75x11.25': { label: '1¾×11¼ LVL', maxSpan: { living: 264, sleeping: 282 } },
      '1.75x14':    { label: '1¾×14 LVL',   maxSpan: { living: 324, sleeping: 348 } },
    },
  },
  i_joist: {
    label: 'I-Joist',
    sizes: {
      '9.5':  { label: '9½" I-Joist',  maxSpan: { living: 198, sleeping: 216 } },
      '11.88': { label: '11⅞" I-Joist', maxSpan: { living: 252, sleeping: 270 } },
      '14':   { label: '14" I-Joist',   maxSpan: { living: 306, sleeping: 330 } },
      '16':   { label: '16" I-Joist',   maxSpan: { living: 354, sleeping: 378 } },
    },
  },
  floor_truss: {
    label: 'Floor Truss',
    sizes: {
      '12': { label: '12" Floor Truss', maxSpan: { living: 228, sleeping: 246 } },
      '14': { label: '14" Floor Truss', maxSpan: { living: 276, sleeping: 300 } },
      '16': { label: '16" Floor Truss', maxSpan: { living: 324, sleeping: 348 } },
      '18': { label: '18" Floor Truss', maxSpan: { living: 360, sleeping: 390 } },
      '24': { label: '24" Floor Truss', maxSpan: { living: 432, sleeping: 462 } },
    },
  },
};

// Spacing options
export const JOIST_SPACING_OPTIONS = [
  { value: 12, label: '12" OC' },
  { value: 16, label: '16" OC' },
  { value: 24, label: '24" OC' },
];

export const GRADE_OPTIONS = [
  { value: '#1', label: '#1' },
  { value: '#2', label: '#2' },
];

// Beam & header sizes (includes 2x for headers + 4x and 6x for beams)
export const BEAM_SIZES = {
  '2x6':  { label: '2×6',  width: 1.5, depth: 5.5,  S: 7.56,   I: 20.80,  weight: 1.7 },
  '2x8':  { label: '2×8',  width: 1.5, depth: 7.25, S: 13.14,  I: 47.63,  weight: 2.3 },
  '2x10': { label: '2×10', width: 1.5, depth: 9.25, S: 21.39,  I: 98.93,  weight: 2.9 },
  '2x12': { label: '2×12', width: 1.5, depth: 11.25, S: 31.64, I: 177.98, weight: 3.5 },
  '4x6':  { label: '4×6',  width: 3.5, depth: 5.5,  S: 17.65,  I: 48.53,  weight: 4.0 },
  '4x8':  { label: '4×8',  width: 3.5, depth: 7.25, S: 30.66,  I: 111.15, weight: 5.3 },
  '4x10': { label: '4×10', width: 3.5, depth: 9.25, S: 49.91,  I: 230.84, weight: 6.7 },
  '4x12': { label: '4×12', width: 3.5, depth: 11.25, S: 73.83, I: 415.28, weight: 8.2 },
  '6x6':  { label: '6×6',  width: 5.5, depth: 5.5,  S: 27.73,  I: 76.26,  weight: 6.3 },
  '6x8':  { label: '6×8',  width: 5.5, depth: 7.5,  S: 51.56,  I: 193.36, weight: 8.6 },
  '6x10': { label: '6×10', width: 5.5, depth: 9.5,  S: 82.73,  I: 392.96, weight: 10.9 },
  '6x12': { label: '6×12', width: 5.5, depth: 11.5, S: 121.23, I: 697.07, weight: 13.1 },
};

export const BEAM_LOAD_TYPES = {
  header_1story: { label: 'Header — 1 Story', live: 30, dead: 15, deflectionLimit: 360, description: 'Window/door header, 1 floor above' },
  header_2story: { label: 'Header — 2 Story', live: 30, dead: 25, deflectionLimit: 360, description: 'Header with 2 floors above' },
  floor_beam:    { label: 'Floor Beam',        live: 40, dead: 15, deflectionLimit: 360, description: 'Beam supporting floor joists' },
  roof_beam:     { label: 'Roof Beam',         live: 20, dead: 15, deflectionLimit: 240, description: 'Beam supporting roof only' },
  barn_header:   { label: 'Barn Door Header',  live: 20, dead: 20, deflectionLimit: 240, description: 'Large opening in post-frame' },
  deck_beam:     { label: 'Deck Beam',         live: 40, dead: 10, deflectionLimit: 360, description: 'Exterior deck support beam' },
};

export const ENGINEERED_BEAMS = {
  lvl: {
    label: 'LVL (Laminated Veneer Lumber)',
    Fb: 2600, E: 1900000,
    sizes: {
      '1.75x7.25':  { label: '1¾×7¼ LVL',  width: 1.75, depth: 7.25,  S: 15.36, I: 55.68 },
      '1.75x9.25':  { label: '1¾×9¼ LVL',  width: 1.75, depth: 9.25,  S: 24.96, I: 115.44 },
      '1.75x11.25': { label: '1¾×11¼ LVL', width: 1.75, depth: 11.25, S: 36.91, I: 207.63 },
      '1.75x14':    { label: '1¾×14 LVL',   width: 1.75, depth: 14,    S: 57.17, I: 400.17 },
      '1.75x16':    { label: '1¾×16 LVL',   width: 1.75, depth: 16,    S: 74.67, I: 597.33 },
      '1.75x18':    { label: '1¾×18 LVL',   width: 1.75, depth: 18,    S: 94.50, I: 850.50 },
      '3.5x7.25':   { label: '3½×7¼ LVL',  width: 3.5,  depth: 7.25,  S: 30.72, I: 111.36 },
      '3.5x9.25':   { label: '3½×9¼ LVL',  width: 3.5,  depth: 9.25,  S: 49.92, I: 230.88 },
      '3.5x11.25':  { label: '3½×11¼ LVL', width: 3.5,  depth: 11.25, S: 73.83, I: 415.28 },
      '3.5x14':     { label: '3½×14 LVL',   width: 3.5,  depth: 14,    S: 114.33, I: 800.33 },
    },
  },
  psl: {
    label: 'PSL (Parallel Strand Lumber)',
    Fb: 2900, E: 2000000,
    sizes: {
      '3.5x7':      { label: '3½×7 PSL',    width: 3.5,  depth: 7,     S: 28.58, I: 100.04 },
      '3.5x9.25':   { label: '3½×9¼ PSL',  width: 3.5,  depth: 9.25,  S: 49.92, I: 230.88 },
      '3.5x11.25':  { label: '3½×11¼ PSL', width: 3.5,  depth: 11.25, S: 73.83, I: 415.28 },
      '3.5x14':     { label: '3½×14 PSL',   width: 3.5,  depth: 14,    S: 114.33, I: 800.33 },
      '5.25x7':     { label: '5¼×7 PSL',    width: 5.25, depth: 7,     S: 42.88, I: 150.06 },
      '5.25x9.25':  { label: '5¼×9¼ PSL',  width: 5.25, depth: 9.25,  S: 74.88, I: 346.32 },
      '5.25x11.25': { label: '5¼×11¼ PSL', width: 5.25, depth: 11.25, S: 110.74, I: 622.92 },
    },
  },
};

export const PLY_OPTIONS = [
  { value: 1, label: 'Single' },
  { value: 2, label: 'Double (2-ply)' },
  { value: 3, label: 'Triple (3-ply)' },
];
